import { Checkbox } from "@/components/ui/checkbox";

const SERIF = { fontFamily: '"Times New Roman", Times, "Liberation Serif", serif' };

const Field = ({ label, value, onChange, readOnly, testId, type = "text" }) => (
    <label className="flex flex-wrap items-end gap-x-2 gap-y-0.5 py-1">
        <span className="font-bold whitespace-nowrap">{label}</span>
        <input
            type={type}
            value={value || ""}
            readOnly={readOnly}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 min-w-[180px] min-h-0 border-0 border-b border-black/70 bg-amber-50/40 px-1 py-0.5 outline-none focus:bg-amber-100/60 read-only:bg-transparent"
            style={SERIF}
            data-testid={testId}
        />
    </label>
);

const Check = ({ checked, onChange, readOnly, testId, children }) => (
    <label className="flex items-start gap-3 py-1 cursor-pointer">
        <Checkbox
            checked={!!checked}
            disabled={readOnly}
            onCheckedChange={(v) => onChange(!!v)}
            className="mt-1.5 h-4 w-4 min-h-0 shrink-0 self-start rounded-none border-black data-[state=checked]:bg-black disabled:opacity-100 disabled:cursor-default"
            data-testid={testId}
        />
        <span className="flex-1">{children}</span>
    </label>
);

export const DataConsentSheet = ({ doc, text, set, readOnly, dateLabel }) => {
    const person = doc.contact_choice === "person";
    return (
        <div className="mx-auto max-w-[680px] bg-white text-black shadow-sm border border-black/10 px-6 sm:px-12 py-10 text-[15px] sm:text-[17px] leading-[1.2]" style={SERIF} data-testid="data-consent-sheet">
            <div className="pl-3">
                <img src="/consent/logo.png" alt="Espacio de Neurorrehabilitación Boreal" className="w-14 h-14" />
                <img src="/consent/header.png" alt="Espacio de Neurorrehabilitación Boreal · C/ Pechuán 14, 28002 Madrid" className="w-48 mt-1" />
            </div>
            <div className="text-center font-bold text-[11px] tracking-wide mt-4 mb-2" style={{ color: "#385623" }}>{text.title}</div>
            <div className="space-y-1 text-justify">
                {text.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="pl-3 mt-3 space-y-1">
                <Field label="Nombre del usuario:" value={doc.user_name} onChange={(v) => set({ user_name: v })} readOnly={readOnly} testId="dc-user-name" />
                <Field label="DNI:" value={doc.user_dni} onChange={(v) => set({ user_dni: v })} readOnly={readOnly} testId="dc-user-dni" />
                <Field label="Domicilio:" value={doc.user_address} onChange={(v) => set({ user_address: v })} readOnly={readOnly} testId="dc-user-address" />
                <Field label="Correo electrónico:" type="email" value={doc.user_email} onChange={(v) => set({ user_email: v })} readOnly={readOnly} testId="dc-user-email" />
                <div className="pt-3 space-y-1 text-justify">
                    <Check checked={doc.consent_contact} readOnly={readOnly} testId="dc-consent-contact"
                        onChange={(v) => set(v ? { consent_contact: true, contact_choice: "person" } : { consent_contact: false })}>
                        {text.checks.consent_contact}
                    </Check>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-1 py-1">
                        <Check checked={person} readOnly={readOnly} testId="dc-contact-person"
                            onChange={(v) => set({ contact_choice: v ? "person" : null })}>
                            <span className="whitespace-nowrap">Nombre del familiar /cuidador:</span>
                        </Check>
                        <input
                            value={doc.contact_name || ""}
                            readOnly={readOnly}
                            onChange={(e) => set({ contact_name: e.target.value, contact_choice: "person" })}
                            className="flex-1 min-w-[160px] min-h-0 border-0 border-b border-black/70 bg-amber-50/40 px-1 py-0.5 outline-none focus:bg-amber-100/60 read-only:bg-transparent"
                            style={SERIF}
                            data-testid="dc-contact-name"
                        />
                        <Check checked={doc.contact_choice === "none"} readOnly={readOnly} testId="dc-contact-none"
                            onChange={(v) => set(v ? { contact_choice: "none", consent_contact: false, contact_name: "" } : { contact_choice: null })}>
                            Ninguna
                        </Check>
                    </div>
                    <Check checked={doc.consent_doctors} readOnly={readOnly} testId="dc-consent-doctors" onChange={(v) => set({ consent_doctors: v })}>
                        {text.checks.consent_doctors}
                    </Check>
                    <Check checked={doc.consent_marketing} readOnly={readOnly} testId="dc-consent-marketing" onChange={(v) => set({ consent_marketing: v })}>
                        {text.checks.consent_marketing}
                    </Check>
                </div>
            </div>
            <div className="mt-8" data-testid="dc-date">En Madrid, a la fecha: {dateLabel}</div>
            <div className="text-right text-[11px] mt-1">Firma del usuario</div>
            <div className="mx-auto mt-2 w-52 h-20 border border-black flex items-center justify-center" data-testid="dc-signature-box">
                {doc.signature_data_url
                    ? <img src={doc.signature_data_url} alt="Firma" className="max-h-[72px] max-w-[196px]" data-testid="dc-signature-img" />
                    : <span className="text-[11px] text-black/40">Pendiente de firma</span>}
            </div>
            {doc.signer_type === "representative" && doc.signed_by_name && (
                <div className="text-center text-[12px] mt-1" data-testid="dc-representative-line">
                    Firmado por {doc.signed_by_name}, en calidad de tutor/representante
                </div>
            )}
        </div>
    );
};
